import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './flameco-ui/flameco-ui.css';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { RoleProvider } from './contexts/RoleContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import RoleGuard from './components/Auth/RoleGuard';
import AuthCallback from './components/Auth/AuthCallback';
import AppFrame from './components/Layout/AppFrame';
import { RequireTag } from './flameco/FlamecoAuthProvider';
import { Content, EmptyState, StateScreen } from './flameco-ui';
import { ModuleId } from './types/auth';
import { isTiaApp } from './utils/appShell';

// Route-level code splitting: each tool loads only when opened. Critical for the
// Flameco TIA Android app, which should download TIA chunks only, not every
// internal tool, over shop-floor WiFi/cellular.
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const MTOConfigurator = lazy(() => import('./components/MTOConfigurator/MTOConfigurator'));
const FlameCellCalculator = lazy(() => import('./components/FlameCellCalculator/FlameCellCalculator'));
const ProductionPlanning = lazy(() => import('./components/ProductionPlanning/ProductionPlanning'));
const ThreadedInlineArrestors = lazy(() => import('./components/ThreadedInlineArrestors/ThreadedInlineArrestors'));
const TribalKnowledge = lazy(() => import('./components/TribalKnowledge/TribalKnowledge'));
const LaserQuote = lazy(() => import('./components/LaserQuote/LaserQuote'));
const BalancedStrategicPlan = lazy(() => import('./components/BalancedStrategicPlan/BalancedStrategicPlan'));
const UserManagement = lazy(() => import('./components/UserManagement/UserManagement'));

const Callback = () => {
  const navigate = useNavigate();
  return <AuthCallback onDone={to => navigate(to, { replace: true })} />;
};

const NoAccess = () => (
  <Content>
    <div className="fl-card">
      <EmptyState title="You don't have access to this" text="Ask an administrator to give you access." />
    </div>
  </Content>
);

/** A tool page: the app role (old matrix) AND the Flameco tag must both allow it. */
const tool = (moduleId: ModuleId, tag: string, page: React.ReactNode) => (
  <RoleGuard moduleId={moduleId}>
    <RequireTag tag={tag} fallback={<NoAccess />}>
      {page}
    </RequireTag>
  </RoleGuard>
);

function App() {
  // Flameco TIA app shell: the Android app is THIS site, locked to the TIA tool.
  // Every non-auth route collapses onto /threaded-inline-arrestors; there is no
  // dashboard, no Flameco frame, and no other tool is reachable from inside the app.
  const tiaApp = isTiaApp();

  return (
    <AuthProvider>
      <RoleProvider>
        <Router>
          <div className="App">
            <Suspense fallback={<StateScreen title="Loading…" busy />}>
              <Routes>
                <Route path="/auth/callback" element={<Callback />} />
                {/* Old sign-in pages: sign-in is Flameco's hosted page now. */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/forgot-password" element={<Navigate to="/" replace />} />
                <Route path="/reset-password" element={<Navigate to="/" replace />} />
                {tiaApp ? (
                  <>
                    {['/flame-arrestors', '/threaded-inline-arrestors'].map(path => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <ProtectedRoute>
                            {tool('threaded-inline-arrestors', 'flame-arrestors.view', <ThreadedInlineArrestors />)}
                          </ProtectedRoute>
                        }
                      />
                    ))}
                    <Route path="*" element={<Navigate to="/threaded-inline-arrestors" replace />} />
                  </>
                ) : (
                  <Route
                    element={
                      <ProtectedRoute>
                        <AppFrame />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/" element={<Dashboard />} />
                    {/* Flame Arrestors module. /threaded-inline-arrestors kept as an alias:
                        the Android app loads it and old links/scenarios point at it. */}
                    <Route
                      path="/flame-arrestors"
                      element={tool('threaded-inline-arrestors', 'flame-arrestors.view', <ThreadedInlineArrestors />)}
                    />
                    <Route
                      path="/threaded-inline-arrestors"
                      element={tool('threaded-inline-arrestors', 'flame-arrestors.view', <ThreadedInlineArrestors />)}
                    />
                    <Route
                      path="/mto-configurator"
                      element={tool('mto-configurator', 'mto-configurator.view', <MTOConfigurator />)}
                    />
                    <Route
                      path="/flame-cell-calculator"
                      element={tool('flame-cell-calculator', 'flame-cell-calculator.view', <FlameCellCalculator />)}
                    />
                    <Route
                      path="/production-planning"
                      element={tool(
                        'production-planning',
                        'production-planning.view',
                        <div id="planner-root">
                          <ProductionPlanning />
                        </div>,
                      )}
                    />
                    <Route path="/laser-quote" element={tool('laser-quote', 'laser-quote.view', <LaserQuote />)} />
                    <Route
                      path="/balanced-strategic-plan"
                      element={tool('balanced-strategic-plan', 'balanced-strategic-plan.view', <BalancedStrategicPlan />)}
                    />
                    <Route
                      path="/tribal-knowledge"
                      element={tool('tribal-knowledge', 'tribal-knowledge.view', <TribalKnowledge />)}
                    />
                    <Route
                      path="/user-management"
                      element={tool('user-management', 'user-management.view', <UserManagement />)}
                    />
                  </Route>
                )}
              </Routes>
            </Suspense>
          </div>
        </Router>
      </RoleProvider>
    </AuthProvider>
  );
}

export default App;
